import { readConfig, writeConfig } from "./config";
import { deleteSecret, getSecret, setSecret } from "./secret-store";
import { refreshAccessToken } from "./oauth";
import type { AuthMethod, Identity, Profile } from "./types";

/** 60-second buffer — refresh before the token actually expires. */
const EXPIRY_BUFFER_MS = 60_000;

/**
 * The OAuth secret stored in the keychain: the long-lived refresh token plus a
 * cached short-lived access token and its expiry. Kept together as one keychain
 * entry so the access token never lands in the plaintext config file, and so a
 * rotated refresh token is written atomically with the access token it pairs
 * with (the backend invalidates the old refresh token on rotation).
 */
interface OAuthSecret {
	refreshToken: string;
	accessToken?: string;
	tokenExpiresAt?: string;
}

/** Parses the stored keychain secret, tolerating the legacy plain refresh-token string. */
function parseOAuthSecret(raw: string): OAuthSecret {
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (
			parsed != null &&
			typeof parsed === "object" &&
			typeof (parsed as OAuthSecret).refreshToken === "string"
		) {
			return parsed as OAuthSecret;
		}
	} catch {
		// Not JSON — fall through to the legacy plain refresh-token form.
	}
	return { refreshToken: raw };
}

/**
 * Returns a valid Bearer token for the given profile, handling both auth
 * methods transparently:
 *  - api_key: returns the raw API key from the keychain.
 *  - oauth: returns the cached access token if still valid, otherwise uses the
 *    stored refresh token to obtain a new one and persists it to the keychain.
 */
export async function getValidAccessToken(
	profile: Profile,
): Promise<string | null> {
	if (profile.method === "api_key") {
		return getSecret(profile.name);
	}

	const raw = getSecret(profile.name);
	if (!raw) return null;
	const secret = parseOAuthSecret(raw);

	if (secret.accessToken && secret.tokenExpiresAt) {
		const expiresAt = new Date(secret.tokenExpiresAt).getTime();
		if (Date.now() < expiresAt - EXPIRY_BUFFER_MS) {
			return secret.accessToken;
		}
	}

	let refreshed: Awaited<ReturnType<typeof refreshAccessToken>>;
	try {
		refreshed = await refreshAccessToken(secret.refreshToken);
	} catch {
		// Refresh token expired/revoked, or a transient network failure. Surface
		// it as "no usable credential" (null) so the caller returns the clean
		// "Run `whop login` again" 401 instead of crashing with a raw stack trace.
		return null;
	}
	const tokenExpiresAt = new Date(
		Date.now() + refreshed.expiresIn * 1000,
	).toISOString();

	setSecret(
		profile.name,
		JSON.stringify({
			refreshToken: refreshed.refreshToken,
			accessToken: refreshed.accessToken,
			tokenExpiresAt,
		} satisfies OAuthSecret),
	);

	return refreshed.accessToken;
}

/**
 * Profile orchestration — the only module that writes both the config file and
 * the keychain, keeping them consistent.
 */

export function listProfiles(env: NodeJS.ProcessEnv = process.env): Profile[] {
	return readConfig(env).profiles;
}

export function getActiveProfile(
	env: NodeJS.ProcessEnv = process.env,
): Profile | null {
	const config = readConfig(env);
	if (!config.activeProfile) return null;
	return (
		config.profiles.find((profile) => profile.name === config.activeProfile) ??
		null
	);
}

function slugify(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 32);
}

/** Picks a default profile name from the identity, slugified: email local-part → route → title → id → fallback. */
export function deriveProfileName(
	identity: Identity,
	fallback = "default",
): string {
	if (identity.email) return slugify(identity.email.split("@")[0]) || fallback;
	if (identity.route) return slugify(identity.route) || fallback;
	if (identity.title) return slugify(identity.title) || fallback;
	if (identity.id) return identity.id;
	return fallback;
}

/** Adds or replaces a profile (dedupe by name), stores its secret, and makes it active. */
export function upsertProfile(
	input: {
		name: string;
		method: AuthMethod;
		secret: string;
		identity: Identity;
		/** OAuth only: the business account associated with the OAuth user, for scoped API calls */
		accountIdentity?: Identity;
		/** OAuth only: cached access token */
		accessToken?: string;
		/** OAuth only: ISO expiry timestamp for accessToken */
		tokenExpiresAt?: string;
	},
	env: NodeJS.ProcessEnv = process.env,
): Profile {
	const config = readConfig(env);
	const existing = config.profiles.find(
		(profile) => profile.name === input.name,
	);
	const isOauth = input.method === "oauth";
	const account = isOauth ? input.accountIdentity : input.identity;
	const profile: Profile = {
		name: input.name,
		method: input.method,
		accountId: account?.id,
		accountTitle: account?.title,
		accountRoute: account?.route,
		userId: isOauth ? input.identity.id : undefined,
		userEmail: isOauth ? input.identity.email : undefined,
		createdAt: existing?.createdAt ?? new Date().toISOString(),
	};
	config.profiles = [
		...config.profiles.filter((p) => p.name !== input.name),
		profile,
	];
	config.activeProfile = input.name;
	writeConfig(config, env);
	// oauth secrets are stored as a JSON blob (refresh + cached access token);
	// api_key stores the raw key. The access token never touches the config file.
	const secret =
		input.method === "oauth"
			? JSON.stringify({
					refreshToken: input.secret,
					accessToken: input.accessToken,
					tokenExpiresAt: input.tokenExpiresAt,
				} satisfies OAuthSecret)
			: input.secret;
	setSecret(input.name, secret);
	return profile;
}

/**
 * Points a profile at a different business account — used when a command
 * creates a business account mid-run (e.g. quickstart) or switches the default
 * (auth account) so follow-up commands act on it without a re-login. Pass
 * `profileName` to pin the write to the profile validated at the start of the
 * command, so a concurrent `auth switch`/`logout` can't redirect it; defaults
 * to whichever profile is active at write time. Returns false when the target
 * profile no longer exists (env-key auth has nothing to persist).
 */
export function setActiveProfileAccount(
	identity: Identity,
	profileName?: string,
	env: NodeJS.ProcessEnv = process.env,
): boolean {
	const config = readConfig(env);
	const target = profileName ?? config.activeProfile;
	const profile = config.profiles.find(
		(candidate) => candidate.name === target,
	);
	if (!profile) return false;
	profile.accountId = identity.id;
	profile.accountTitle = identity.title;
	profile.accountRoute = identity.route;
	writeConfig(config, env);
	return true;
}

/** Removes a profile and its secret; if it was active, the first remaining profile becomes active. */
export function removeProfile(
	name: string,
	env: NodeJS.ProcessEnv = process.env,
): { removed: boolean; newActive: string | null } {
	const config = readConfig(env);
	const existed = config.profiles.some((profile) => profile.name === name);
	config.profiles = config.profiles.filter((profile) => profile.name !== name);
	if (config.activeProfile === name) {
		config.activeProfile = config.profiles[0]?.name;
	}
	writeConfig(config, env);
	deleteSecret(name);
	return { removed: existed, newActive: config.activeProfile ?? null };
}

/** Removes every profile and its keychain secret — a full auth reset. Returns the removed names. */
export function removeAllProfiles(
	env: NodeJS.ProcessEnv = process.env,
): string[] {
	const config = readConfig(env);
	const names = config.profiles.map((profile) => profile.name);
	if (names.length === 0) return names;
	config.profiles = [];
	config.activeProfile = undefined;
	writeConfig(config, env);
	for (const name of names) deleteSecret(name);
	return names;
}

/** Makes a profile active. Returns null if no such profile exists. */
export function switchProfile(
	name: string,
	env: NodeJS.ProcessEnv = process.env,
): Profile | null {
	const config = readConfig(env);
	const profile = config.profiles.find((p) => p.name === name);
	if (!profile) return null;
	config.activeProfile = name;
	writeConfig(config, env);
	return profile;
}
